import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import Socket from '../../services/SocketService';
import browserHistory from '../../history';
import LoadingPage from '../LoadingPage';
import Form from '../form/Form';
import PageService from '../../services/PageService';
import { modal } from '../modal/ModalEmitter';
import ApiService from '../../services/ApiService';

// Save or Submit label
const useSaveLabel = ['SMM1'];

class ListEditContainer extends Component {
  constructor(props) {
    super(props);
    this.createItem = this.createItem.bind(this);
    this.updateItem = this.updateItem.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.createCallback = this.createCallback.bind(this);
    this.updateCallback = this.updateCallback.bind(this);
    this.handleChange = this.handleChange.bind(this);

    this.state = {
      isLocked: false,
      isReady: false,
      layout: [],
      page: {},
    };

    this.itemIdDefaults = {
      pattern: /^[\d\w]+$/,
      required: true,
      data: props.itemId === '_new' ? '' : props.itemId,
    };

    this.lockedByMe = false;
  }

  componentDidMount() {
    const { itemName, itemId, user } = this.props;
    const { branch } = user;
    const lockId = `${branch.mvAccount}-${itemName}-${itemId}`;

    Socket.global.on(`${lockId}.lock`, (lockedBy = {}) => {
      if (!this.state.isLocked && this.state.isReady && user.id !== lockedBy.id) {
        this.setState({ isLocked: true });
        this.showLockedDialog(lockedBy);
      }
    });

    this.refresh(itemName, itemId);
  }

  componentWillUnmount() {
    const { itemName, itemId, user } = this.props;
    const { branch } = user;
    const lockId = `${branch.mvAccount}-${itemName}-${itemId}`;

    Socket.global.off(`${lockId}.lock`);

    if (this.lockedByMe) {
      PageService.unlockPage(itemName, itemId);
    }
  }

  getItemIdFromLayout() {
    const { itemId, action } = this.props;
    const { layout } = this.state;

    if (!this.hasItemId()) {
      return {};
    }

    const firstField = layout[0];

    Object.keys(this.itemIdDefaults).forEach(key => {
      firstField[key] = firstField[key] !== undefined ? firstField[key] : this.itemIdDefaults[key];
    });

    // Auto will not take a value
    if (firstField.auto) {
      delete firstField.required;
      delete firstField.pattern;

      if (action === 'copy') {
        firstField.data = '';
      }
    }

    if (!this.isCreationAction() && firstField.data !== itemId) {
      firstField.data = itemId;
    }

    // Currently editing a record will render the itemid field hidden
    // this will force it to be a regular input to avoid weirdness when it's not
    if (!this.isCreationAction()) {
      firstField.control = 'input';
    }

    return layout[0];
  }

  hasItemId(layout = this.state.layout) {
    if (layout[0] && layout[0].name === 'itemid') {
      return true;
    }

    return false;
  }

  /**
   * Retrieves layout and data for list item.  Also gets locking status.
   *
   * @param {String} itemName - Identifier for dataset.
   * @param {String} itemId - Identifier for record in dataset.
   */
  refresh(itemName, itemId) {
    const { branchId } = this.props;

    const getLockStatus = () => {
      if (this.isCreationAction()) {
        this.setState({ isReady: true });
        return;
      }

      PageService.getLock(itemName, itemId).then(response => {
        const lockedBy = response.data.user;

        if (lockedBy) {
          this.lockedByMe = lockedBy.username === this.props.user.username;
        }

        const isLocked = lockedBy !== null && !this.lockedByMe;

        this.setState({
          isLocked,
          isReady: true,
        });

        if (isLocked) {
          this.showLockedDialog(lockedBy);
        }
      });
    };

    this.setState({ layout: [], isReady: false });

    ApiService.get(`list/${itemName}/${encodeURIComponent(itemId || '_new')}`, {
      params: { useBranchId: branchId },
    }).then(({ data }) => {
      // TODO: This is officially bad.  This is because the page is described
      // in a yaml and a json file.
      const page = _.get(data, 'page') || {};
      page.page = _.get(data, 'yaml.page') || {};

      this.setState({ page }, () => {
        const layout = _.get(data, 'page_layout') || [];

        if (this.hasItemId(layout)) {
          if (!this.isCreationAction() || layout[0].auto) {
            layout[0].hidden = true;
          }
        } else {
          console.warn('itemid is not defined');
        }

        this.setState({ layout }, getLockStatus);
      });
    });
  }

  /**
   * Handler for cancel button.
   */
  handleCancel() {
    browserHistory.goBack();
  }

  /**
   * Callback after createItem request has been completed.  New itemId is
   * in the response.
   *
   * @param {Object} response - Response object of create request.
   */
  createCallback(response) {
    const { itemName } = this.props;
    const itemId = _.get(response, 'data.responses[0].itemid');
    const hasRedirect = _.get(response, 'data.redirect');

    if (hasRedirect) {
      return;
    }

    if (itemId) {
      browserHistory.replace(`/list/${itemName}/${encodeURIComponent(itemId)}/edit`);
      this.refresh(itemName, itemId);
    } else {
      browserHistory.replace(`/list/${itemName}`);
    }
  }

  /**
   * Request to be performed when creating a new record.
   *
   * @param {Object} payload - The form values.
   */
  createItem(payload) {
    const { itemName, branchId } = this.props;

    let newItemId = 'new';

    // Item id is user entered so it's used in the POST/PUT request
    if (this.hasItemId() && !this.state.layout[0].auto) {
      const itemIdIndex = _.findIndex(payload, o => o.name === 'itemid');
      newItemId = payload[itemIdIndex].data;
      payload.splice(itemIdIndex, 1);
    }

    // I'm not sure if this is necessary now
    const newPayload = payload.map(field => {
      field.name = field.name.replace(/new/g, newItemId);
      return field;
    });

    const useBranchId = branchId ? `?useBranchId=${branchId}` : '';

    return ApiService.post(`list/${itemName}/${encodeURIComponent(newItemId)}${useBranchId}`, newPayload);
  }

  /**
   * Request to be performed when updating an existing record.
   *
   * @param {Object} payload - The form values.
   */
  updateItem(payload) {
    const { itemName, itemId, branchId } = this.props;

    const useBranchId = branchId ? `?useBranchId=${branchId}` : '';

    return ApiService.put(`list/${itemName}/${encodeURIComponent(itemId)}${useBranchId}`, payload);
  }

  /**
   * Callback after updateItem request has been completed.
   *
   * @param {Object} response - Response object of create request.
   */
  updateCallback() {
    const { itemName } = this.props;
    browserHistory.push(`/list/${itemName}`);
  }

  /**
   * Callback after handleChange has been completed.
   *
   */
  handleChange() {
    const { itemName, itemId, action } = this.props;

    // TODO Change callback is getting invoked on initialized aggregation values
    // resulting in an automatic lock.
    if (action === 'new' || this.lockedByMe) {
      return false;
    }

    if (!this.state.isLocked) {
      PageService.lockPage(itemName, itemId);
      this.lockedByMe = true;
    }

    return true;
  }

  /**
   * Callback after handleChange has been completed.
   *
   * @param {Object} lockedBy - User object of who owns the lock.
   */
  showLockedDialog(lockedBy = {}) {
    const { itemName, itemId } = this.props;

    modal.open('pageLocked', {
      hidePrint: true,
      render: 'pageLocked',
      style: 'center',
      title: 'Notice',
      confirm: true,
      size: 'md',
      confirmLabels: ['Force Unlock', 'Close'],
      callback: () => {
        PageService.unlockPage(itemName, itemId);
        PageService.lockPage(itemName, itemId);
        this.refresh(itemName, itemId);
      },
      lockedBy,
    });
  }

  /**
   * Checks page key for a specific option/flag turned on.
   *
   * @param {String} name - Option to check.
   * @returns {Boolean}
   */
  isOption(name = '') {
    const { page } = this.state;

    const options = _.get(page, 'page.Options');

    if (!options) {
      return false;
    }

    if (_.indexOf(options, name) === -1) {
      return false;
    }

    return true;
  }

  /**
   * Checks to see if this form is a simple edit or process.
   *
   * @returns {Boolean}
   */
  isProcess() {
    const { page } = this.state;

    if (page.page.ValidTypes && page.page.ValidTypes[0] === 'process') {
      return true;
    }

    return false;
  }

  /**
   * Determines what type of action the form is making.
   *
   * @return {Boolean}
   */
  isCreationAction() {
    const { action } = this.props;

    return action === 'new' || action === 'copy';
  }

  render() {
    const { branchId, itemName, query } = this.props;

    if (!this.state.isReady) {
      return <LoadingPage />;
    }

    const submitAll = this.isProcess() || this.isOption('SubmitAll') || this.isCreationAction();
    const feedback = this.isProcess() ? 'modal' : 'toaster';

    // Override handlers through defined props
    const handleCancel = this.props.handleCancel || this.handleCancel;

    const submitCallback = this.isCreationAction()
      ? this.props.submitCallback || this.createCallback
      : this.props.submitCallback || this.updateCallback;

    return (
      <Form
        action={this.isProcess() ? 'process' : this.props.action}
        branchId={branchId}
        className="fill"
        confirmSubmit={this.isOption('ConfirmSubmit')}
        feedback={feedback}
        initialValues={query}
        itemId={this.getItemIdFromLayout()}
        itemName={itemName}
        layout={this.state.layout}
        legacyFormat
        onCancel={handleCancel}
        onChange={this.handleChange}
        page={this.state.page}
        persistTabs={this.props.persistTabs}
        readOnly={this.props.readOnly || this.isOption('ReadOnly') || this.state.isLocked}
        resetOnSubmit={this.isOption('ResetOnSubmit')}
        submit={this.isCreationAction() ? this.createItem : this.updateItem}
        submitAll={submitAll}
        submitCallback={submitCallback}
        submitLabel={useSaveLabel.includes(itemName) ? 'Save' : 'Submit'}
        trackDirty={!this.isCreationAction()}
        usePrefix={this.props.usePrefix}
      />
    );
  }
}

ListEditContainer.defaultProps = {
  action: 'edit',
  itemId: '_new',
  itemName: null,
  persistTabs: true,
  usePrefix: '',
  query: {},
};

ListEditContainer.propTypes = {
  action: PropTypes.string,
  branchId: PropTypes.string,
  handleCancel: PropTypes.func,
  itemId: PropTypes.string,
  itemName: PropTypes.string,
  persistTabs: PropTypes.bool,
  query: PropTypes.object,
  submitCallback: PropTypes.func,
  usePrefix: PropTypes.string,
  user: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    user: state.app.user,
  };
}

export default connect(mapStateToProps)(ListEditContainer);
